// Helpers to render an unbounded JSON questionnaire as a flat list of editable
// fields. The shape varies per agent run, so we walk the tree recursively and
// produce one field per scalar leaf, plus one JSON textarea per array.
//
// `dotPath` (e.g. `unternehmen.firmenname`) is used as the form-field key and
// also to write user input back into the JSON tree on save.

import type { Questionnaire } from "./api";

export type LeafKind = "string" | "json";

export interface QuestionnaireField {
  path: string;
  label: string;
  kind: LeafKind;
  initialValue: string;
}

export interface QuestionnaireSection {
  /** Top-level key, e.g. "meta", "unternehmen". */
  key: string;
  label: string;
  fields: QuestionnaireField[];
}

export const SECTION_LABELS: Record<string, string> = {
  meta: "Meta (Berater, Zeitraum)",
  unternehmen: "Unternehmen",
  makro: "Makro",
  mikro: "Mikro",
  wettbewerb: "Wettbewerb",
  marketing_mix: "Marketing-Mix",
  sales_kanal: "Sales-Kanal",
  icp: "ICP",
  ews: "EWS",
  schwachstellen: "Schwachstellen",
  muendliche_absprachen: "Mündliche Absprachen",
};

export function flattenQuestionnaire(data: Questionnaire): QuestionnaireSection[] {
  const sections: QuestionnaireSection[] = [];

  function fieldsFor(value: unknown, path: string, fields: QuestionnaireField[]) {
    if (value === null || value === undefined) return;
    if (Array.isArray(value)) {
      fields.push({
        path,
        label: leafLabel(path),
        kind: "json",
        initialValue: JSON.stringify(value, null, 2),
      });
      return;
    }
    if (typeof value === "object") {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        fieldsFor(v, path ? path + "." + k : k, fields);
      }
      return;
    }
    fields.push({
      path,
      label: leafLabel(path),
      kind: "string",
      initialValue: value == null ? "" : String(value),
    });
  }

  for (const [key, value] of Object.entries(data)) {
    if (Array.isArray(value)) {
      // Top-level arrays still get a section wrapper for visual consistency.
      sections.push({
        key,
        label: SECTION_LABELS[key] ?? key,
        fields: [
          {
            path: key,
            label: leafLabel(key),
            kind: "json",
            initialValue: JSON.stringify(value, null, 2),
          },
        ],
      });
      continue;
    }
    if (value && typeof value === "object") {
      const fields: QuestionnaireField[] = [];
      fieldsFor(value, key, fields);
      sections.push({ key, label: SECTION_LABELS[key] ?? key, fields });
      continue;
    }
    sections.push({
      key,
      label: SECTION_LABELS[key] ?? key,
      fields: [
        {
          path: key,
          label: leafLabel(key),
          kind: "string",
          initialValue: value == null ? "" : String(value),
        },
      ],
    });
  }

  return sections;
}

function leafLabel(path: string): string {
  const tail = path.split(".").pop() ?? path;
  return tail;
}

export function setByPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    const next = cur[p];
    if (next && typeof next === "object" && !Array.isArray(next)) {
      cur = next as Record<string, unknown>;
    } else {
      cur[p] = {};
      cur = cur[p] as Record<string, unknown>;
    }
  }
  cur[parts[parts.length - 1]] = value;
}

/**
 * Build a flat `path → value` map from the user-edited form, parsing JSON
 * fields back into structured data.
 */
export function collectFormValues(
  values: Record<string, string>,
  fields: QuestionnaireField[],
): Questionnaire {
  const result: Record<string, unknown> = {};
  for (const f of fields) {
    const raw = (values[f.path] ?? "").trim();
    if (f.kind === "json") {
      try {
        setByPath(result, f.path, raw === "" ? [] : JSON.parse(raw));
      } catch {
        setByPath(result, f.path, raw);
      }
    } else {
      setByPath(result, f.path, raw);
    }
  }
  return result as Questionnaire;
}

export function flatFieldList(sections: QuestionnaireSection[]): QuestionnaireField[] {
  return sections.flatMap((s) => s.fields);
}

export function initialValuesFromSections(sections: QuestionnaireSection[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of sections) {
    for (const f of s.fields) out[f.path] = f.initialValue;
  }
  return out;
}
